import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { type Message } from "@/types";
import { toast } from "@/components/ui/use-toast";
import { format as formatDateFns, isToday, isYesterday } from 'date-fns';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

export function getChatTitle(messages: Message[]): string {
  const firstMessage = messages.find(m => m.role === 'user');
  if (!firstMessage) return 'New Chat';
  return truncateText(firstMessage.content, 30);
}

export function parseCommand(message: string): { text: string; commands: string[] } {
  const commandPattern = /\/(\w+)/g;
  const commands: string[] = [];

  // Extract all commands
  let match;
  while ((match = commandPattern.exec(message)) !== null) {
    commands.push(match[1]);
  }

  // Remove all commands from the message for clean text
  const text = message.replace(commandPattern, '').trim();

  return { text, commands };
}

export function executePayment(amount: number): Promise<{ success: boolean }> {
  // Mock function for now, will integrate with Stripe later
  return new Promise((resolve) => {
    setTimeout(() => {
      if (Math.random() > 0.1) {
        toast({
          title: "Payment successful",
          description: `You've been charged $${amount.toFixed(2)}`,
        });
        resolve({ success: true });
      } else {
        toast({
          title: "Payment failed",
          description: "Please try again or contact support",
          variant: "destructive",
        });
        resolve({ success: false });
      }
    }, 1500);
  });
}

export function formatCodeForDisplay(code: string, language: string): string {
  // This would be enhanced with a syntax highlighting library
  return `\`\`\`${language}\n${code}\n\`\`\``;
}

// Format a timestamp into a relative time (e.g., "2 minutes ago")
export function formatRelativeTime(timestamp: string): string {
  console.log('Formatting timestamp:', timestamp);
  const date = new Date(timestamp);
  const now = new Date();
  
  console.log('Parsed date:', date);
  console.log('Current date:', now);
  console.log('Timestamp type:', typeof timestamp);
  
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) {
    return 'just now';
  }
  
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }
  
  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  }
  
  const years = Math.floor(months / 12);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
}

export function formatLobbyTimestamp(input: string | Date): string {
  const date = new Date(input);
  if (isNaN(date.getTime())) return '';

  if (isToday(date)) {
    return formatDateFns(date, 'p');
  }

  if (isYesterday(date)) {
    return `Yesterday • ${formatDateFns(date, 'p')}`;
  }

  return formatDateFns(date, 'dd/MM/yyyy');
}

export function downloadFile(content: string, filename: string, type = 'text/plain') {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function cleanMarkdownBold(text: string): string {
  // Regex to find ** followed by spaces, then content, then spaces, then **
  // And replace it with **content**
  return text.replace(/\*\*(\s*)(.*?)(\s*)\*\*/g, '**$2**');
}